import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { api } from "../lib/api";
import BentoRoster from "../components/BentoRoster";
import { fmtHeight, fmtCmIn, fmtShoe, specText } from "../lib/measure";

/**
 * /models — Board page in the IMG Paris / Elite tradition.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────┐
 *   │  ● Amsterdam                              🔎 search │
 *   ├───┬─────────────────────────────────────────────────┤
 *   │ A │                                                 │
 *   │ B │                                                 │
 *   │ C │             5-column portrait grid              │
 *   │ … │             (name under each tile)              │
 *   │ Z │                                                 │
 *   └───┴─────────────────────────────────────────────────┘
 *
 * The A-Z rail on the left is sticky; clicking a letter scrolls to the first
 * talent whose first name starts with that letter. Disabled letters (no
 * matching talent) render at 20% opacity.
 */

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function Models() {
  const [params, setParams] = useSearchParams();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);

  const q = (params.get("q") || "").toLowerCase();
  const gridRef = useRef(null);
  const tileRefs = useRef({}); // id → HTMLElement

  const board = params.get("board") === "new-faces" ? "new-faces" : "";

  useEffect(() => {
    setLoading(true);
    api.get("/models").then((r) => {
      const list = r.data || [];
      // Sort alphabetically by first name for a proper board index
      list.sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
      setModels(list);
      setLoading(false);
    });
  }, []);

  const update = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  const visible = useMemo(
    () => models.filter(
      (m) => (!board || (m.board || "main") === board)
        && (!q || m.name.toLowerCase().includes(q))
    ),
    [models, q, board]
  );

  // Set of first-letters that currently have visible talents — used to dim
  // the disabled alphabet entries.
  const activeLetters = useMemo(() => {
    const s = new Set();
    for (const m of visible) {
      const c = (m.name || "").trim().charAt(0).toUpperCase();
      if (c) s.add(c);
    }
    return s;
  }, [visible]);

  const scrollToLetter = (letter) => {
    // Find the first talent whose first name starts with this letter
    const target = visible.find((m) => (m.name || "").trim().charAt(0).toUpperCase() === letter);
    if (!target) return;
    const el = tileRefs.current[target.id];
    if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  return (
    <div data-testid="models-page" className="pt-24 lg:pt-28 pb-24 bg-white">
      {/* Top bar — clean, no city pin */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between py-4 border-b border-black/10">
          {/* Board label + New Faces toggle */}
          <div className="flex items-center gap-7">
            <span className="label-overline text-black pb-1 border-b border-black">The board</span>
            <span className="hidden sm:block w-px h-4 bg-black/15"/>
            <button
              type="button"
              data-testid="board-new-faces"
              onClick={() => update("board", board ? "" : "new-faces")}
              className={`label-overline pb-1 border-b transition-colors ${
                board
                  ? "text-black border-black"
                  : "text-zinc-400 border-transparent hover:text-zinc-500"
              }`}
            >
              new faces
            </button>
          </div>

          {/* Search — expands on click, minimal magnifier when closed */}
          <div className="flex items-center gap-3">
            {searchOpen || q ? (
              <div className="flex items-center border border-black/10 focus-within:border-black transition-colors">
                <Search size={14} className="ml-3 text-zinc-500 shrink-0"/>
                <input
                  data-testid="filter-search"
                  autoFocus
                  placeholder="Search by name…"
                  defaultValue={q}
                  onChange={(e) => update("q", e.target.value)}
                  className="bg-transparent outline-none px-2 py-1.5 text-sm w-48 md:w-64 placeholder:text-zinc-400"
                />
                <button
                  type="button"
                  onClick={() => { update("q", ""); setSearchOpen(false); }}
                  className="pr-2 pl-1 text-zinc-500 hover:text-black"
                  aria-label="Clear search"
                >
                  <X size={14}/>
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="filter-search-toggle"
                onClick={() => setSearchOpen(true)}
                aria-label="Search talents"
                className="w-9 h-9 flex items-center justify-center border border-black/10 hover:border-black transition-colors rounded-full"
              >
                <Search size={14}/>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main content — alphabet rail + grid */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 mt-8 lg:mt-10 flex gap-6 lg:gap-10">
        {/* Alphabet rail — hidden on small mobile, sticky on ≥ md */}
        <nav
          aria-label="Alphabet index"
          className="hidden md:flex flex-col items-center pt-2 sticky top-28 self-start text-[10px] uppercase tracking-widest select-none"
        >
          {ALPHABET.map((letter) => {
            const active = activeLetters.has(letter);
            return (
              <button
                key={letter}
                type="button"
                onClick={() => scrollToLetter(letter)}
                disabled={!active}
                data-testid={`alpha-${letter}`}
                className={`px-2 py-[3px] transition-colors ${
                  active
                    ? "text-zinc-700 hover:text-black cursor-pointer"
                    : "text-zinc-300 cursor-not-allowed"
                }`}
              >
                {letter}
              </button>
            );
          })}
        </nav>

        {/* Grid — takes the rest */}
        <div ref={gridRef} className="flex-1 min-w-0" data-testid="board-grid">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-4 md:gap-x-6 gap-y-10 md:gap-y-14">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i}>
                  <div className="bg-[#F2F2F1] animate-pulse aspect-[3/4]"/>
                  <div className="mt-3 h-4 w-20 bg-[#F2F2F1] animate-pulse"/>
                  <div className="mt-1 h-4 w-14 bg-[#F2F2F1] animate-pulse"/>
                </div>
              ))}
            </div>
          ) : visible.length === 0 ? (
            <div className="py-32 text-center text-zinc-500 label-overline">
              {q
                ? "No talents match your search."
                : "The board is being curated. Check back soon."}
            </div>
          ) : (
            <BentoRosterIndexed models={visible} registerRef={(id, el) => { tileRefs.current[id] = el; }}/>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Wrapper around BentoRoster that also registers a ref for each tile so the
 * alphabet rail can scroll-into-view precisely. Keeping this thin adapter here
 * avoids polluting the reusable BentoRoster component with page-specific logic.
 */
function BentoRosterIndexed({ models, registerRef }) {
  // Touch devices have no hover: first tap reveals measurements, second tap opens the profile.
  const [tapped, setTapped] = useState(null);
  const isTouch = () => window.matchMedia("(hover: none)").matches;
  const onTileClick = (e, slug) => {
    if (!isTouch() || tapped === slug) return;
    e.preventDefault();
    setTapped(slug);
  };
  return (
    <div
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-4 md:gap-x-6 gap-y-10 md:gap-y-14"
    >
      {models.map((m) => {
        const parts = (m.name || "").trim().split(/\s+/);
        const first = parts[0] || "";
        const last = parts.slice(1).join(" ");
        const hoverImg = (m.gallery || []).find((src) => src && src !== m.cover_image);
        const active = tapped === m.slug;
        return (
          <a
            key={m.id}
            ref={(el) => registerRef(m.id, el)}
            href={`/models/${m.slug}`}
            data-testid={`bento-${m.slug}`}
            data-active={active ? "true" : undefined}
            onClick={(e) => onTileClick(e, m.slug)}
            className="group block focus:outline-none focus:ring-1 focus:ring-black scroll-mt-32"
          >
            <div className="relative aspect-[3/4] overflow-hidden bg-[#F2F2F1]">
              <img
                className="absolute inset-0 w-full h-full object-cover object-[center_18%] transition-transform duration-700 group-hover:scale-[1.02]"
                src={m.cover_image}
                alt={m.name}
                loading="lazy"
              />
              {hoverImg && (
                <img
                  className={`absolute inset-0 w-full h-full object-cover object-[center_18%] transition-opacity duration-500 group-hover:opacity-100 ${active ? "opacity-100" : "opacity-0"}`}
                  src={hoverImg}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                />
              )}
              <MeasurementsOverlay m={m} active={active}/>
            </div>
            <div className="mt-3 leading-tight">
              <p className="font-serif text-lg md:text-xl text-[#0A0A0A]">{first}</p>
              <p className="font-serif text-lg md:text-xl text-[#0A0A0A]">
                {last || <span className="text-transparent select-none">.</span>}
              </p>
            </div>
          </a>
        );
      })}
    </div>
  );
}
/* Hover card — measurements slide up over the photo (IMG / Elite board behaviour).
   `active` = revealed by a first tap on touch devices. */
function MeasurementsOverlay({ m, active = false }) {
  const s = m.measurements || {};
  const rows = [
    ["Height", specText(fmtHeight(s.height_cm))],
    ["Bust", specText(fmtCmIn(s.bust_cm))],
    ["Waist", specText(fmtCmIn(s.waist_cm))],
    ["Hips", specText(fmtCmIn(s.hips_cm))],
    ["Shoe", specText(fmtShoe(s.shoe_eu))],
    ["Hair", s.hair || "—"],
    ["Eyes", s.eyes || "—"],
  ];
  const shown = active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2";
  return (
    <div
      data-testid={`bento-specs-${m.slug}`}
      className={`absolute inset-x-0 bottom-0 px-3 pb-3 pt-14 bg-gradient-to-t from-black/85 via-black/55 to-transparent text-white ${shown} group-hover:opacity-100 group-hover:translate-y-0 group-focus-visible:opacity-100 transition-all duration-300 pointer-events-none`}
    >
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 sm:gap-y-1">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-2 border-b border-white/15 pb-0.5">
            <dt className="text-[9px] uppercase tracking-[0.18em] text-white/60">{label}</dt>
            <dd className="text-[11px] md:text-xs whitespace-nowrap">{value}</dd>
          </div>
        ))}
      </dl>
      {active && (
        <p data-testid={`bento-tap-hint-${m.slug}`} className="mt-2 text-[9px] uppercase tracking-[0.18em] text-white/70 whitespace-nowrap">
          Tap again for profile
        </p>
      )}
    </div>
  );
}

// (BentoRoster kept as-is for potential future reuse; this page uses the
// indexed variant above so the A-Z rail can scroll to specific tiles.)
export { BentoRoster };
